/**
 * NILE Country/Language Redirect System
 * 
 * This script handles:
 * 1. Cross-domain cookie management for country/language preferences
 * 2. Automatic redirect to correct store based on saved preferences
 * 3. Country detection for first-time visitors
 * 4. Language selection based on browser/country defaults
 */

(function() {
  'use strict';

  // ===== [NILE-DEBUG] temporary diagnostics — remove after debugging =====
  try {
    console.log('[NILE-DEBUG] page-load', {
      href: window.location.href,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      referrer: document.referrer,
      cookie: document.cookie || '(empty)',
      shopDomain: window.shopDomain,
      shopDefaultLocale: window.shopDefaultLocale
    });
    // form.submit() (the method) does NOT fire this event; this only catches
    // real/native form submissions (e.g. a localization form posting normally).
    document.addEventListener('submit', function (e) {
      console.log('[NILE-DEBUG] NATIVE FORM SUBMIT', {
        action: (e.target && e.target.action) || null,
        id: (e.target && e.target.id) || null,
        host: window.location.hostname
      });
    }, true);
    window.addEventListener('pagehide', function () {
      console.log('[NILE-DEBUG] pagehide — leaving', window.location.href);
    });
  } catch (e) {}
  // ===== end [NILE-DEBUG] =====

  // ============================================
  // CONFIGURATION
  // ============================================

  // Per-store default locale (injected via theme.liquid). Default lang gets no /<lang>/ prefix.
  const SHOP_DEFAULT_LOCALE = ((typeof window !== 'undefined' && window.shopDefaultLocale) || 'de').toLowerCase();

  const COOKIE_NAME = 'nile_prefs';
  const COOKIE_EXPIRY_DAYS = 730; // 2 years
  
  // Store configuration - maps store IDs to their countries and URLs
  const STORE_CONFIG = {
    chf: {
      countries: ['CH', 'LI', 'CA'],
      productionDomains: ['nile.ch', 'www.nile.ch'],
      testDomains: ['nile-ch.myshopify.com', 'nile-online-chf.myshopify.com']
    },
    eur: {
      countries: ['AT', 'BE', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
      productionDomains: ['eu.nile.ch'],
      testDomains: ['nile-online-eur.myshopify.com', 'nile-online-eur-test.myshopify.com']
    },
    gbp: {
      countries: ['GB'],
      productionDomains: ['nile-online-gbp-prod.myshopify.com', 'gb.nile.ch'],
      testDomains: ['nile-online-test-gbp.myshopify.com']
    }
  };

  // Per-store default locale (served at the URL root with no /<lang>/ prefix).
  // The GBP store is English-only.
  const STORE_DEFAULT_LOCALE = { chf: 'de', eur: 'de', gbp: 'en' };

  // Country to default language mapping
  const COUNTRY_DEFAULT_LANGUAGE = {
    // German-speaking countries
    CH: 'de', DE: 'de', AT: 'de', LI: 'de',
    // French-speaking countries
    FR: 'fr', BE: 'fr', LU: 'fr',
    // Italian-speaking
    IT: 'it',
    // English-speaking
    GB: 'en', IE: 'en', CA: 'en',
    // Default for all others
    default: 'de'
  };

  const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'];

  // ============================================
  // COOKIE MANAGEMENT
  // ============================================

  /**
   * Get the cookie domain for cross-subdomain sharing
   * Returns .nile.ch for production, or current domain for test environments
   */
  function getCookieDomain() {
    const hostname = window.location.hostname;
    
    // For nile.ch and subdomains (eu.nile.ch, gb.nile.ch), use .nile.ch
    if (hostname.endsWith('nile.ch')) {
      return '.nile.ch';
    }
    
    // For myshopify.com domains, cookies can't be shared across stores
    // Return empty string to use current domain only
    return '';
  }

  /**
   * Set the preferences cookie
   * @param {Object} prefs - { country, language, store, ts }
   */
  function setNilePrefs(prefs) {
    const data = {
      country: prefs.country,
      language: prefs.language,
      store: prefs.store,
      ts: Date.now()
    };
    
    const expires = new Date();
    expires.setTime(expires.getTime() + (COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
    
    const cookieDomain = getCookieDomain();
    let cookieString = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}`;
    cookieString += `; expires=${expires.toUTCString()}`;
    cookieString += '; path=/';
    
    if (cookieDomain) {
      cookieString += `; domain=${cookieDomain}`;
    }
    
    cookieString += '; SameSite=Lax';
    
    // Only add Secure flag on HTTPS
    if (window.location.protocol === 'https:') {
      cookieString += '; Secure';
    }
    
    document.cookie = cookieString;
  }

  /**
   * Get the preferences from cookie ONLY (no localStorage fallback)
   * @returns {Object|null} - { country, language, store, ts } or null if not found
   */
  function getNilePrefs() {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === COOKIE_NAME && value) {
          return JSON.parse(decodeURIComponent(value));
        }
      }
    } catch (e) {
      console.warn('Error reading nile_prefs cookie:', e);
    }
    
    return null;
  }

  /**
   * Clear the preferences cookie
   */
  function clearNilePrefs() {
    const cookieDomain = getCookieDomain();
    let cookieString = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    
    if (cookieDomain) {
      cookieString += `; domain=${cookieDomain}`;
    }
    
    document.cookie = cookieString;
    
    // Also clear without domain (for myshopify.com environments)
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }

  // ============================================
  // STORE DETECTION
  // ============================================

  /**
   * Determine which store the user is currently on
   * @returns {string} - 'chf', 'eur', 'gbp', or 'unknown'
   */
  function getCurrentStore() {
    const hostname = window.location.hostname;
    
    for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
      if (config.productionDomains.includes(hostname) || config.testDomains.includes(hostname)) {
        return storeId;
      }
    }
    
    // Fallback: check window.shopDomain if available
    if (window.shopDomain) {
      for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
        if (config.testDomains.includes(window.shopDomain)) {
          return storeId;
        }
      }
    }
    
    return 'unknown';
  }

  /**
   * Determine which store a country belongs to
   * @param {string} countryCode - ISO country code (e.g., 'DE', 'CH')
   * @returns {string} - 'chf', 'eur', 'gbp', or 'unknown'
   */
  function getStoreForCountry(countryCode) {
    if (!countryCode) return 'unknown';
    
    const code = countryCode.toUpperCase();
    
    for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
      if (config.countries.includes(code)) {
        return storeId;
      }
    }
    
    return 'unknown';
  }

  /**
   * Check if we're on a test/development environment
   * @returns {boolean}
   */
  function isTestEnvironment() {
    const hostname = window.location.hostname;
    return hostname.includes('myshopify.com') || hostname.includes('localhost');
  }

  /**
   * Get the URL for a specific store
   * @param {string} storeId - 'chf', 'eur', or 'gbp'
   * @returns {string|null} - The store URL or null if not found
   */
  function getStoreUrl(storeId) {
    const config = STORE_CONFIG[storeId];
    if (!config) return null;
    
    const isTest = isTestEnvironment();
    
    if (isTest) {
      // For test environments, use themeSettings URLs if available
      if (window.themeSettings) {
        if (storeId === 'chf') {
          return window.themeSettings.test_ch_store_url || window.themeSettings.ch_store_url;
        } else if (storeId === 'eur') {
          return window.themeSettings.test_eu_store_url || window.themeSettings.eu_store_url;
        } else if (storeId === 'gbp') {
          return window.themeSettings.test_gbp_store_url || window.themeSettings.gbp_store_url;
        }
      }
      // Fallback to test domains
      return config.testDomains[0] ? `https://${config.testDomains[0]}` : null;
    } else {
      // Production
      if (window.themeSettings) {
        if (storeId === 'chf') {
          return window.themeSettings.ch_store_url || `https://${config.productionDomains[0]}`;
        } else if (storeId === 'eur') {
          return window.themeSettings.eu_store_url || `https://${config.productionDomains[0]}`;
        } else if (storeId === 'gbp') {
          return window.themeSettings.gbp_store_url || `https://${config.productionDomains[0]}`;
        }
      }
      return config.productionDomains[0] ? `https://${config.productionDomains[0]}` : null;
    }
  }

  // ============================================
  // LANGUAGE DETECTION
  // ============================================

  /**
   * Get the browser's preferred language (simplified to supported languages)
   * @returns {string} - 'de', 'en', 'fr', 'it', or 'de' as fallback
   */
  function getBrowserLanguage() {
    const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];
    
    for (const lang of browserLanguages) {
      const baseLang = lang.split('-')[0].toLowerCase();
      if (SUPPORTED_LANGUAGES.includes(baseLang)) {
        return baseLang;
      }
    }
    
    return SHOP_DEFAULT_LOCALE;
  }

  /**
   * Get the default language for a country, considering browser preference
   * @param {string} countryCode - ISO country code
   * @returns {string} - Language code
   */
  function getDefaultLanguage(countryCode) {
    const browserLang = getBrowserLanguage();
    
    // Browser language takes priority if it's supported
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
    
    // Fall back to country mapping, then to this store's default.
    return COUNTRY_DEFAULT_LANGUAGE[countryCode] || SHOP_DEFAULT_LOCALE;
  }

  /**
   * Get the current language from URL path
   * @returns {string} - 'de', 'en', 'fr', 'it'
   */
  function getCurrentLanguageFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/^\/(en|fr|it)(\/|$)/);
    return match ? match[1] : SHOP_DEFAULT_LOCALE;
  }

  // ============================================
  // REDIRECT LOGIC
  // ============================================

  /**
   * Build the redirect URL for a different store
   * @param {string} targetStoreId - The store to redirect to
   * @param {string} language - The language to use
   * @param {string} countryCode - The country code to pass
   * @returns {string|null} - The full redirect URL or null
   */
  function buildRedirectUrl(targetStoreId, language, countryCode) {
    const targetBaseUrl = getStoreUrl(targetStoreId);
    if (!targetBaseUrl) return null;
    
    // Preserve the current page across stores. Strip a leading language segment
    // only when it is a full segment (followed by "/" or end) so handles like
    // "/engraved" are kept. If the page is missing on the target store it 404s,
    // and the 404 fallback in theme.liquid sends the visitor to the homepage.
    let currentPath = window.location.pathname;
    currentPath = currentPath.replace(/^\/(en|fr|it)(?=\/|$)/, '');
    if (!currentPath || currentPath === '') currentPath = '/';

    // Decide the language for the TARGET store. The GBP store is English-only,
    // so anything redirecting there must land on English (its default, no prefix).
    const targetDefaultLocale = STORE_DEFAULT_LOCALE[targetStoreId] || SHOP_DEFAULT_LOCALE;
    // Carry the saved language; the target store applies it via modal_language
    // (below) only if it publishes that language.
    let targetLanguage = language;

    // Prefix a language only for stores known to publish de/en/fr/it (chf/eur).
    // gbp's locale set is not assumed: keep the bare path and let modal_language
    // upgrade it if published — avoids a /<lang> 404 when not published on gbp.
    let newPath = currentPath;
    if (targetStoreId !== 'gbp' && targetLanguage && targetLanguage !== targetDefaultLocale) {
      newPath = currentPath === '/' ? `/${targetLanguage}` : `/${targetLanguage}${currentPath}`;
    }
    
    // Construct the URL
    let targetUrl = targetBaseUrl.replace(/\/$/, '') + newPath;
    
    // Add query params
    const url = new URL(targetUrl);
    url.searchParams.set('redirected', 'true');
    url.searchParams.set('country', countryCode);
    if (targetLanguage) {
      url.searchParams.set('modal_language', targetLanguage);
    }

    // Preserve other query params
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (key !== 'redirected' && key !== 'country' && key !== 'modal_language' && key !== 'modal_country') {
        url.searchParams.set(key, value);
      }
    });
    
    return url.toString();
  }

  /**
   * Perform redirect to the correct store
   * @param {string} targetStoreId - The store to redirect to
   * @param {string} language - The language to use
   * @param {string} countryCode - The country code
   */
  function redirectToStore(targetStoreId, language, countryCode) {
    const redirectUrl = buildRedirectUrl(targetStoreId, language, countryCode);
    console.log('[NILE-DEBUG] redirectToStore -> navigating', {
      targetStoreId, language, countryCode, redirectUrl, from: window.location.href
    });
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }

  // ============================================
  // COUNTRY DETECTION
  // ============================================

  /**
   * Get detected country from Shopify (passed via theme.liquid)
   * @returns {string|null} - ISO country code or null
   */
  function getShopifyDetectedCountry() {
    return window.nileDetectedCountry || null;
  }

  /**
   * Fetch country from ipinfo.io as fallback
   * @returns {Promise<string|null>} - ISO country code or null
   */
  async function fetchCountryFromIpInfo() {
    try {
      const response = await fetch('https://ipinfo.io/json', { timeout: 5000 });
      const data = await response.json();
      return data.country || null;
    } catch (e) {
      console.warn('Could not fetch country from ipinfo.io:', e);
      return null;
    }
  }

  /**
   * Detect the user's country using available methods
   * @returns {Promise<string|null>} - ISO country code or null
   */
  async function detectCountry() {
    // Try Shopify's detection first (most reliable, no API call)
    const shopifyCountry = getShopifyDetectedCountry();
    if (shopifyCountry) {
      return shopifyCountry;
    }
    
    // Fallback to ipinfo.io
    return await fetchCountryFromIpInfo();
  }

  // ============================================
  // MAIN INITIALIZATION
  // ============================================

  /**
   * Check if user should be redirected based on saved preferences
   * This runs early on page load
   */
  function checkAndRedirect() {
    // If we arrived via an explicit cross-store redirect (?redirected=true), do
    // NOT auto-redirect again based on a saved cookie. The user just explicitly
    // chose this store; a stale/conflicting cookie must not bounce them off it.
    // The destination's modal handler reconciles the cookie to this store.
    const __params = new URLSearchParams(window.location.search);
    if (__params.get('redirected') === 'true') {
      console.log('[NILE-DEBUG] checkAndRedirect: redirected=true -> skip auto-redirect');
      return false;
    }

    const prefs = getNilePrefs();
    console.log('[NILE-DEBUG] checkAndRedirect', {
      prefs: prefs,
      currentStore: getCurrentStore(),
      hostname: window.location.hostname,
      shopDomain: window.shopDomain
    });

    if (!prefs || !prefs.country || !prefs.store) {
      // No saved preferences, let modal handle first-time visitors
      return false;
    }

    const currentStore = getCurrentStore();

    // If already on correct store, no redirect needed
    if (currentStore === prefs.store || currentStore === 'unknown') {
      return false;
    }

    // User has preferences and is on wrong store - redirect automatically
    console.log(`[NILE-DEBUG] checkAndRedirect: REDIRECTING from ${currentStore} to ${prefs.store} for country ${prefs.country}`);
    redirectToStore(prefs.store, prefs.language, prefs.country);
    return true;
  }

  /**
   * Handle first-time visitor detection
   * Called when no cookie exists
   * @returns {Promise<Object>} - { country, language, store, needsModal }
   */
  async function handleFirstTimeVisitor() {
    const detectedCountry = await detectCountry();
    
    if (!detectedCountry) {
      return { country: null, language: SHOP_DEFAULT_LOCALE, store: 'unknown', needsModal: true };
    }
    
    const targetStore = getStoreForCountry(detectedCountry);
    const currentStore = getCurrentStore();
    const language = getDefaultLanguage(detectedCountry);
    
    const result = {
      country: detectedCountry,
      language: language,
      store: targetStore,
      needsModal: currentStore !== targetStore && targetStore !== 'unknown'
    };
    
    // If on correct store, save preferences silently (no modal needed)
    if (!result.needsModal && targetStore !== 'unknown') {
      setNilePrefs({
        country: detectedCountry,
        language: language,
        store: targetStore
      });
    }
    
    return result;
  }

  // ============================================
  // PUBLIC API
  // ============================================

  // Expose functions globally for use by other scripts
  window.NileCountryRedirect = {
    // Cookie management
    setNilePrefs: setNilePrefs,
    getNilePrefs: getNilePrefs,
    clearNilePrefs: clearNilePrefs,
    
    // Store detection
    getCurrentStore: getCurrentStore,
    getStoreForCountry: getStoreForCountry,
    getStoreUrl: getStoreUrl,
    isTestEnvironment: isTestEnvironment,
    
    // Language helpers
    getBrowserLanguage: getBrowserLanguage,
    getDefaultLanguage: getDefaultLanguage,
    getCurrentLanguageFromUrl: getCurrentLanguageFromUrl,
    SUPPORTED_LANGUAGES: SUPPORTED_LANGUAGES,
    
    // Country detection
    detectCountry: detectCountry,
    getShopifyDetectedCountry: getShopifyDetectedCountry,
    
    // Redirect functions
    buildRedirectUrl: buildRedirectUrl,
    redirectToStore: redirectToStore,
    
    // First-time visitor handling
    handleFirstTimeVisitor: handleFirstTimeVisitor,
    
    // Configuration
    STORE_CONFIG: STORE_CONFIG,
    COUNTRY_DEFAULT_LANGUAGE: COUNTRY_DEFAULT_LANGUAGE
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  // Skip the auto-redirect inside the Shopify theme editor. The editor preview
  // iframe must stay on the store/page being edited; redirecting it (or showing
  // the country modal) makes content editing impossible. The public API above
  // stays exposed for any other consumers. designMode is true only in the editor.
  const inThemeEditor = !!(window.Shopify && window.Shopify.designMode);

  // Run redirect check immediately (before DOM is ready)
  // This ensures fast redirects for returning users
  const wasRedirected = inThemeEditor ? false : checkAndRedirect();

  // Store the redirect state for the modal script to check
  window.nileRedirectInProgress = wasRedirected;

})();
